/**
 * 율리우스일 변환 및 Delta-T 계산.
 *
 * 출처: Jean Meeus, "Astronomical Algorithms", 2nd edition, Chapter 7.
 * 그레고리력 날짜와 율리우스일 간 변환, TT/UT 변환용 Delta-T 계산을 제공한다.
 */

/**
 * 그레고리력 날짜를 율리우스일로 변환한다.
 *
 * Jean Meeus "Astronomical Algorithms" Chapter 7 알고리즘.
 *
 * @param year - 양력 연도
 * @param month - 월 (1-12)
 * @param day - 일 (소수점으로 시간 포함 가능, 예: 1.5 = 1일 정오)
 * @returns 율리우스일
 */
export function gregorianToJD(year: number, month: number, day: number): number {
  let y = year;
  let m = month;
  if (m <= 2) {
    y -= 1;
    m += 12;
  }
  const A = Math.floor(y / 100);
  const B = 2 - A + Math.floor(A / 4);
  return Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + day + B - 1524.5;
}

/**
 * 날짜·시간 구성요소를 율리우스일로 변환한다.
 *
 * @param year - 양력 연도
 * @param month - 월 (1-12)
 * @param day - 일
 * @param hour - 시 (0-23, 기본값 0)
 * @param minute - 분 (0-59, 기본값 0)
 * @param second - 초 (0-59, 기본값 0)
 * @returns 율리우스일
 */
export function dateToJD(
  year: number,
  month: number,
  day: number,
  hour: number = 0,
  minute: number = 0,
  second: number = 0,
): number {
  const dayFraction = day + (hour + minute / 60 + second / 3600) / 24;
  return gregorianToJD(year, month, dayFraction);
}

/**
 * 율리우스일을 그레고리력 날짜 구성요소로 역변환한다.
 *
 * Jean Meeus, "Astronomical Algorithms", 2nd ed., Chapter 7 기반.
 *
 * @param jd - 율리우스일
 * @returns year, month, day, hour, minute, second 객체
 */
export function jdToGregorian(jd: number): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const jdPlus = jd + 0.5;
  const Z = Math.floor(jdPlus);
  const F = jdPlus - Z;

  let A: number;
  if (Z < 2299161) {
    A = Z;
  } else {
    const alpha = Math.floor((Z - 1867216.25) / 36524.25);
    A = Z + 1 + alpha - Math.floor(alpha / 4);
  }

  const B = A + 1524;
  const C = Math.floor((B - 122.1) / 365.25);
  const D = Math.floor(365.25 * C);
  const E = Math.floor((B - D) / 30.6001);

  const dayDecimal = B - D - Math.floor(30.6001 * E) + F;
  const day = Math.floor(dayDecimal);
  const month = E < 14 ? E - 1 : E - 13;
  const year = month > 2 ? C - 4716 : C - 4715;

  const dayFrac = dayDecimal - day;
  const totalSeconds = dayFrac * 86400;
  const hour = Math.floor(totalSeconds / 3600);
  const minute = Math.floor((totalSeconds % 3600) / 60);
  const second = Math.round(totalSeconds % 60);

  return { year, month, day, hour, minute, second };
}

/**
 * 율리우스일을 JavaScript Date (UTC)로 변환한다.
 *
 * @param jd - 율리우스일
 * @returns UTC 기준 JavaScript Date 객체
 */
export function jdToDate(jd: number): Date {
  const { year, month, day, hour, minute, second } = jdToGregorian(jd);
  return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
}

/**
 * 지정 날짜의 정오(12:00 UT) 율리우스일을 반환한다.
 *
 * 일주 계산에 사용한다.
 * gregorianToJD에 정수 day를 전달하면 자정(civil day 시작) JD(.5로 끝남)가 되므로,
 * 0.5를 더하면 정오 = 정수 JD 값이 된다.
 *
 * @param year - 양력 연도
 * @param month - 월 (1-12)
 * @param day - 일
 * @returns 정오(UT) 율리우스일
 */
export function jdAtNoon(year: number, month: number, day: number): number {
  return gregorianToJD(year, month, day) + 0.5;
}

/**
 * Delta-T (TT - UT) 근사값을 초 단위로 계산한다.
 *
 * 1900-2100년 유효한 다항식 근사.
 * NASA/Espenak 다항식 기반.
 *
 * @param year - 양력 연도 (소수점 연도 가능)
 * @returns Delta-T (초)
 */
export function deltaT(year: number): number {
  if (year < 1900 || year > 2100) {
    // Outside range, use simple extrapolation
    const dt = (year - 2000) / 100;
    return 102 + 102 * dt + 25.3 * dt * dt;
  }

  const t = year - 2000;

  if (year < 1920) {
    const u = (year - 1900) / 100;
    return -2.79 + 149.4119 * u - 598.939 * u * u + 6196.6 * u * u * u - 19700.1 * u * u * u * u;
  } else if (year < 1941) {
    const t1 = year - 1920;
    return 21.20 + 0.84493 * t1 - 0.076100 * t1 * t1 + 0.0020936 * t1 * t1 * t1;
  } else if (year < 1961) {
    const t1 = year - 1950;
    return 29.07 + 0.407 * t1 - t1 * t1 / 233 + t1 * t1 * t1 / 2547;
  } else if (year < 1986) {
    const t1 = year - 1975;
    return 45.45 + 1.067 * t1 - t1 * t1 / 260 - t1 * t1 * t1 / 718;
  } else if (year < 2005) {
    const t1 = year - 2000;
    return (
      63.86 +
      0.3345 * t1 -
      0.060374 * t1 * t1 +
      0.0017275 * t1 * t1 * t1 +
      0.000651814 * t1 * t1 * t1 * t1 +
      0.00002373599 * t1 * t1 * t1 * t1 * t1
    );
  } else if (year < 2050) {
    return 62.92 + 0.32217 * t + 0.005589 * t * t;
  } else if (year < 2150) {
    const u = (year - 1820) / 100;
    return -20 + 32 * u * u - 0.5628 * (2150 - year);
  }

  const u = (year - 1820) / 100;
  return -20 + 32 * u * u;
}

